import React, { useCallback, useEffect, ChangeEvent } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/redux/store';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Table,
    Tbody,
    Td,
    Thead,
    Tr,
    Spinner as SpinnerChakra,
    Button,
} from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa';
import { HiX } from 'react-icons/hi';
import * as yup from 'yup';
import { debounce } from '@/utils/helpers';
import { Pagination } from '@/components/Pagination';
import { ThEl } from '@/features/Quiz/components/ArticlesQuiz';
import { Link } from 'react-router-dom';
import { MyQuizItemV2 } from '@/features/Quiz/components/MyQuizItemV2';
import { useAuth } from '@/context/AuthContext';
import {
    getAllMyQuizzesV2,
    resetQuizMyV2Pages,
    selectQuizMyV2ActivePage,
    selectQuizMyV2Loading,
    selectQuizMyV2Page,
    selectQuizMyV2TotalPages,
    setQuizMyV2ActivePage,
} from '@/redux/reducers/quizzes/quizMyV2';
import Icon from '@/components/Icon';

const searchInputSchema = yup.object().shape({
    keyword: yup.string(),
});

type SearchFormData = {
    keyword: string;
};

export const MyQuiz = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useAuth();
    const isSuperAdmin = user?.roles.includes('ROLE_SUPER_ADMIN');

    const activePage = useSelector(selectQuizMyV2ActivePage, shallowEqual);
    const totalPages = useSelector(selectQuizMyV2TotalPages, shallowEqual);
    const quizzes = useSelector((state: RootState) => selectQuizMyV2Page(state, activePage), shallowEqual);
    const isLoading = useSelector(selectQuizMyV2Loading, shallowEqual);

    const {
        register,
        handleSubmit,
        reset,
        formState: { isSubmitting },
        watch,
        getValues,
        setValue,
    } = useForm<SearchFormData>({
        resolver: yupResolver(searchInputSchema),
        defaultValues: {
            keyword: '',
        },
    });

    const onSearchKeywordSubmit = useCallback(
        (formValues: SearchFormData) => {
            dispatch(resetQuizMyV2Pages());
            dispatch(setQuizMyV2ActivePage(1));
            dispatch(getAllMyQuizzesV2({ activePage: 1, activeSearchKeyword: formValues.keyword }));
        },
        [dispatch]
    );

    const onSearchKeywordChange = debounce(
        useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                e.preventDefault();
                setValue('keyword', e.target.value);
                dispatch(resetQuizMyV2Pages());
                dispatch(setQuizMyV2ActivePage(1));
                dispatch(getAllMyQuizzesV2({ activePage: 1, activeSearchKeyword: e.target.value ?? '' }));
            },
            [dispatch, setValue]
        ),
        300
    );

    const handleReset = useCallback(() => {
        reset();
        dispatch(resetQuizMyV2Pages());
        dispatch(setQuizMyV2ActivePage(1));
        dispatch(getAllMyQuizzesV2({ activePage: 1, activeSearchKeyword: '' }));
    }, [dispatch, reset]);

    const handlePaginationChange = useCallback(
        (page: number) => {
            dispatch(setQuizMyV2ActivePage(page));
        },
        [dispatch]
    );

    useEffect(() => {
        dispatch(getAllMyQuizzesV2({ activePage, activeSearchKeyword: getValues('keyword') ?? '' }));
    }, [dispatch, activePage, getValues]);

    useEffect(
        () => () => {
            dispatch(setQuizMyV2ActivePage(1));
        },
        [dispatch]
    );

    return (
        <Flex w="100%" flexDir="column" mb={'10px'}>
            <Flex gap="20px" sx={{ '.btn-half-width': { flex: '1 0 45%' } }} w="100%">
                <Flex justifyContent={'space-between'} alignItems={'center'} mb={3} w="100%">
                    <form onSubmit={handleSubmit(onSearchKeywordSubmit)}>
                        <InputGroup minW={'400px'} className="input-left-wrap">
                            <InputLeftElement
                                className="InputLeft"
                                pointerEvents={isSubmitting ? 'none' : 'auto'}
                                onClick={handleSubmit(onSearchKeywordSubmit)}>
                                {isSubmitting || isLoading ? <SpinnerChakra size="20px" /> : <FaSearch size="20px" />}
                            </InputLeftElement>
                            <Input
                                placeholder="Search Quiz by Title"
                                bg={'#ffffff'}
                                id={'keyword'}
                                type={'text'}
                                borderRadius="1000px"
                                {...register('keyword')}
                                onChange={onSearchKeywordChange}
                            />
                            {watch('keyword') && (
                                <InputRightElement cursor="pointer" onClick={handleReset}>
                                    <HiX />
                                </InputRightElement>
                            )}
                        </InputGroup>
                    </form>
                    <Button
                        as={Link}
                        variant={'brand'}
                        to={isSuperAdmin ? '/quiz/admin-quiz/create' : '/quiz/my-quiz/create'}>
                        <Icon icon="plus" />
                        &nbsp; Create a New Quiz
                    </Button>
                </Flex>
            </Flex>
            <Flex flexDir={'column'}>
                <Table variant="striped" colorScheme="gray">
                    {quizzes.length > 0 && (
                        <Thead borderBottom="1px solid #e5ebf3">
                            <Tr w="100%">
                                <ThEl align="start" w="65%">
                                    Title
                                </ThEl>
                                <ThEl w="15%">Access</ThEl>
                                <ThEl w="10%" align="start">
                                    Subjects
                                </ThEl>
                                <ThEl w="15%">Questions</ThEl>
                                <ThEl w="15%">Deadline</ThEl>
                                <ThEl w="15%">Actions</ThEl>
                            </Tr>
                        </Thead>
                    )}
                    <Tbody>
                        {quizzes.length > 0 ? (
                            quizzes.map((quiz) => <MyQuizItemV2 key={quiz.id} quiz={quiz} />)
                        ) : (
                            <>
                                {!isLoading && (
                                    <Tr>
                                        <Td>
                                            <Box>No list items</Box>
                                        </Td>
                                    </Tr>
                                )}
                            </>
                        )}
                    </Tbody>
                </Table>

                {totalPages > 1 && (
                    <Flex justifyContent={'end'} mt={'20px'}>
                        <Pagination
                            currentPage={activePage}
                            totalPages={totalPages}
                            handlePagination={handlePaginationChange}
                        />
                    </Flex>
                )}
            </Flex>
        </Flex>
    );
};
